import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { EggHatchAnimation } from './EggHatchAnimation';
const TICKER_NAMES = ['Ananya', 'Rahul', 'Priya', 'Arjun', 'Sneha', 'Karan', 'Divya', 'Vikram', 'Meera', 'Rohan', 'Ishita', 'Aditya'];
const TICKER_LOCALITIES = ['Banjara Hills', 'Jubilee Hills', 'Gachibowli', 'Madhapur', 'Kondapur', 'Hitech City', 'Kukatpally', 'Begumpet'];
const INTEREST_OPTIONS = ['Coffee & Tea', 'Fast Food', 'Healthy Eats', 'Desserts', 'Local Favorites'];
const REFERRAL_SOURCES = ['Instagram', 'TikTok', 'WhatsApp', 'A friend', 'Search', 'Other'];
// To save data: in Google Forms use "Get pre-filled link", swap /viewform for
// /formResponse below, and paste each entry.XXXX id into FORM_ENTRIES.
const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSfIX_md9nk6iRVcIjm8ys74t4xMH1r_N7VgG25SutWsBQLJPQ/formResponse';
const FORM_ENTRIES = {
  name: 'entry.1540860975',
  email: 'entry.1042363886',
  phone: 'entry.1492731773',
  referralSource: 'entry.1764599993',
  interests: 'entry.1240300174',
  referredBy: 'entry.2085574883'
};
interface SignupData {
  name: string;
  email: string;
  phone: string;
  referralSource: string;
  interests: string[];
  referredBy: string;
}
interface SignupStateProps {
  onJoin: (data: SignupData) => void;
}
export function SignupState({
  onJoin
}: SignupStateProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [referredBy, setReferredBy] = useState('');
  // Honeypot: a hidden field real users never see. If a bot fills it, we drop
  // the submission. mountTime powers a time-trap (bots submit near-instantly).
  const [honeypot, setHoneypot] = useState('');
  const mountTime = useRef(Date.now());
  const [errors, setErrors] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Capture the inviter's referral code from the URL (e.g. ?ref=KRAYAARJ482).
  // This is how a referral is attributed: the code rides in the share link,
  // we read it on load, carry it silently through signup, and submit it so the
  // backend can credit the inviter and tally how many friends they brought in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferredBy(ref.trim());
  }, []);
  // Live "just joined" social-proof ticker — a small toast every few seconds
  // with a randomized name + Hyderabad locality + queue number. Purely for
  // FOMO; clears when the signup screen unmounts.
  useEffect(() => {
    const fireTicker = () => {
      const name = TICKER_NAMES[Math.floor(Math.random() * TICKER_NAMES.length)];
      const locality = TICKER_LOCALITIES[Math.floor(Math.random() * TICKER_LOCALITIES.length)];
      const queueNo = (Math.floor(Math.random() * 1200) + 1100).toLocaleString('en-IN');
      toast(`${name} from ${locality} just grabbed #${queueNo} ☕`, {
        duration: 3500,
        style: {
          background: '#fff',
          color: '#000',
          border: '4px solid #000',
          borderRadius: '16px',
          fontFamily: 'Fredoka, sans-serif',
          fontWeight: 600,
          boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)'
        }
      });
    };
    const startTimeout = setTimeout(fireTicker, 5000);
    const interval = setInterval(fireTicker, 6500);
    return () => {
      clearTimeout(startTimeout);
      clearInterval(interval);
    };
  }, []);
  const toggleInterest = (interest: string) => {
    setInterests(prev => prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]);
  };
  // Submit to Google Forms via a hidden <form> targeting a hidden <iframe>.
  // A real form POST (a browser navigation into the iframe) is NOT subject to
  // CORS and is not cancelled when this component unmounts — unlike fetch(),
  // which gets blocked from a sandboxed preview iframe. This is the reliable
  // way to populate a Google Form's Responses from the browser.
  const submitToGoogleForm = (data: SignupData) => {
    return new Promise<void>(resolve => {
      if (GOOGLE_FORM_URL.includes('REPLACE')) {
        resolve();
        return;
      }
      try {
        const iframeName = 'kraya_gforms_sink';
        let iframe = document.getElementById(iframeName) as HTMLIFrameElement | null;
        if (!iframe) {
          iframe = document.createElement('iframe');
          iframe.id = iframeName;
          iframe.name = iframeName;
          iframe.style.display = 'none';
          document.body.appendChild(iframe);
        }
        const form = document.createElement('form');
        form.action = GOOGLE_FORM_URL;
        form.method = 'POST';
        form.target = iframeName;
        form.style.display = 'none';
        const addField = (name: string, value: string) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };
        if (data.name) addField(FORM_ENTRIES.name, data.name);
        if (data.email) addField(FORM_ENTRIES.email, data.email);
        if (data.phone) addField(FORM_ENTRIES.phone, data.phone);
        if (data.referralSource) addField(FORM_ENTRIES.referralSource, data.referralSource);
        if (data.referredBy) addField(FORM_ENTRIES.referredBy, data.referredBy);
        // Checkbox questions: one field per selected choice.
        data.interests.forEach(interest => addField(FORM_ENTRIES.interests, interest));
        document.body.appendChild(form);
        form.submit();
        // Give the navigation a moment to fire, then clean up the form.
        setTimeout(() => {
          form.remove();
          resolve();
        }, 600);
      } catch (error) {
        console.error('Google Forms sync error:', error);
        resolve();
      }
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Bot protection: (1) honeypot tripped (a hidden field only bots fill), or
    // (2) time-trap (submitted in under 3s, too fast for a human). In either
    // case advance to the success screen WITHOUT posting to Google, so junk
    // never reaches the sheet and the bot isn't tipped off that it was blocked.
    const isBot = honeypot.trim() !== '' || Date.now() - mountTime.current < 3000;
    if (isBot) {
      setIsSubmitting(true);
      setTimeout(() => {
        onJoin({
          name,
          email,
          phone,
          referralSource,
          interests,
          referredBy
        });
      }, 300);
      return;
    }
    const newErrors = {
      name: '',
      email: ''
    };
    let hasError = false;
    if (!name.trim()) {
      newErrors.name = 'What should we call you?';
      hasError = true;
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Please enter a valid email.';
      hasError = true;
    }
    setErrors(newErrors);
    if (!hasError) {
      setIsSubmitting(true);
      const data: SignupData = {
        name,
        email,
        phone,
        referralSource,
        interests,
        referredBy
      };
      // Await the Google Forms submission so the network request isn't
      // cancelled when the component unmounts and transitions to success.
      await submitToGoogleForm(data);
      setTimeout(() => {
        onJoin(data);
      }, 300);
    }
  };
  return <motion.div className="w-full max-w-md mx-auto flex flex-col items-center text-center" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.95,
    filter: 'blur(4px)'
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} data-id="element-114">
      <EggHatchAnimation data-id="element-115" />

      <motion.div className="relative mb-8" initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      delay: 0.2,
      bounce: 0.5
    }} data-id="element-116">
        <div className="inline-flex items-center gap-2 bg-[#E2F0A8] text-black px-5 py-2.5 rounded-full font-bold text-sm border-4 border-black neo-shadow" data-id="element-117">
          <Sparkles className="w-4 h-4 text-lime-600" aria-hidden="true" data-id="element-118" />
          Get Ready for Early Access
        </div>
      </motion.div>

      <h2 className="text-5xl md:text-6xl font-bold leading-[1.1] mb-4 uppercase tracking-tight" data-id="element-119">
        Cafe hopping with friends is now made more fun
      </h2>

      <p className="text-lg font-medium mb-8 px-4 opacity-90" data-id="element-120">
        Coffee tastes better when shared. Join Hyderabad's invite-only community
        for café discoveries, perks, and coffee meetups.
      </p>

      <form onSubmit={handleSubmit} className="w-full space-y-4 mb-8" data-id="element-121">
        {/* Honeypot — hidden from humans, catches form-spam bots. Do not remove. */}
        <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] w-px h-px overflow-hidden" data-id="element-122">
          <label htmlFor="kraya-company" data-id="element-123">Company (leave blank)</label>
          <input id="kraya-company" type="text" name="company" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} data-id="element-124" />
        </div>

        {referredBy && <div className="flex items-center gap-3 bg-[#E2F0A8] border-4 border-black rounded-2xl px-4 py-3 text-left neo-shadow" data-id="element-125">
            <PartyPopper className="w-5 h-5 flex-shrink-0 text-lime-700" aria-hidden="true" data-id="element-126" />
            <p className="text-sm font-bold leading-tight" data-id="element-127">
              You were invited! Code{' '}
              <span className="font-mono tracking-wide" data-id="element-128">{referredBy}</span> is
              applied — you'll both climb the line.
            </p>
          </div>}

        <div className="space-y-1 text-left" data-id="element-129">
          <input type="text" placeholder="First Name" value={name} onChange={e => setName(e.target.value)} disabled={isSubmitting} className={`w-full px-5 py-4 rounded-2xl border-4 outline-none transition-all text-lg font-medium
              ${errors.name ? 'border-red-500 bg-red-50' : 'border-black bg-white focus:neo-shadow'}
              disabled:opacity-50`} data-id="element-130" />
          {errors.name && <p className="text-red-600 font-medium text-sm px-2" data-id="element-131">
              {errors.name}
            </p>}
        </div>

        <div className="space-y-1 text-left" data-id="element-132">
          <input type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} disabled={isSubmitting} className={`w-full px-5 py-4 rounded-2xl border-4 outline-none transition-all text-lg font-medium
              ${errors.email ? 'border-red-500 bg-red-50' : 'border-black bg-white focus:neo-shadow'}
              disabled:opacity-50`} data-id="element-133" />
          {errors.email && <p className="text-red-600 font-medium text-sm px-2" data-id="element-134">
              {errors.email}
            </p>}
        </div>

        <div className="space-y-1 text-left" data-id="element-135">
          <label htmlFor="phone" className="block font-bold text-sm px-2 mb-1" data-id="element-136">
            Phone Number{' '}
            <span className="font-medium opacity-60" data-id="element-137">(Optional)</span>
          </label>
          <input id="phone" type="tel" placeholder="Enter your phone number" value={phone} onChange={e => setPhone(e.target.value)} disabled={isSubmitting} className="w-full px-5 py-4 rounded-2xl border-4 border-black bg-white outline-none transition-all text-lg font-medium focus:neo-shadow disabled:opacity-50" data-id="element-138" />
        </div>

        <div className="space-y-1 text-left" data-id="element-139">
          <label htmlFor="referralSource" className="block font-bold text-sm px-2 mb-1" data-id="element-140">
            How did you hear about us?
          </label>
          <select id="referralSource" value={referralSource} onChange={e => setReferralSource(e.target.value)} disabled={isSubmitting} className="w-full px-5 py-4 rounded-2xl border-4 border-black bg-white outline-none transition-all text-lg font-medium focus:neo-shadow disabled:opacity-50 appearance-none cursor-pointer" data-id="element-141">
            <option value="" data-id="element-142">Select an option</option>
            {REFERRAL_SOURCES.map(source => <option key={source} value={source} data-id="element-143">
                {source}
              </option>)}
          </select>
        </div>

        <div className="space-y-2 text-left" data-id="element-144">
          <span className="block font-bold text-base px-2 mb-2" data-id="element-145">
            What interests you?{' '}
            <span className="font-semibold text-black/70" data-id="element-146">
              (Select all that apply)
            </span>
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-147">
            {INTEREST_OPTIONS.map(interest => {
            const checked = interests.includes(interest);
            return <button key={interest} type="button" role="checkbox" aria-checked={checked} onClick={() => toggleInterest(interest)} disabled={isSubmitting} className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl border-4 border-black font-bold text-base transition-all disabled:opacity-50
                    ${checked ? 'bg-kraya-yellow neo-shadow' : 'bg-white hover:neo-shadow'}`} data-id="element-148">
                  <span className={`w-6 h-6 flex-shrink-0 rounded-md border-2 border-black flex items-center justify-center transition-colors
                      ${checked ? 'bg-black text-kraya-yellow' : 'bg-white'}`} data-id="element-149">
                    {checked && <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" aria-hidden="true" data-id="element-150">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-151" />
                      </svg>}
                  </span>
                  {interest}
                </button>;
          })}
          </div>
        </div>

        <div className="space-y-1 text-left" data-id="element-152">
          <label htmlFor="inviteCode" className="block font-bold text-sm px-2 mb-1" data-id="element-153">
            Invite Code{' '}
            <span className="font-medium opacity-60" data-id="element-154">(Optional)</span>
          </label>
          <input id="inviteCode" type="text" placeholder="Got a code from a friend?" value={referredBy} onChange={e => setReferredBy(e.target.value.toUpperCase())} disabled={isSubmitting} className="w-full px-5 py-4 rounded-2xl border-4 border-black bg-white outline-none transition-all text-lg font-mono tracking-wide uppercase focus:neo-shadow disabled:opacity-50 placeholder:font-sans placeholder:normal-case placeholder:tracking-normal" data-id="element-155" />
        </div>

        <motion.button type="submit" disabled={isSubmitting} whileHover={{
        y: -2
      }} whileTap={{
        y: 2,
        scale: 0.98
      }} className="w-full bg-black text-white text-xl font-bold py-4 rounded-2xl border-4 border-black neo-shadow hover:neo-shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:neo-shadow" data-id="element-156">
          {isSubmitting ? <motion.div animate={{
          rotate: 360
        }} transition={{
          repeat: Infinity,
          duration: 1,
          ease: 'linear'
        }} className="w-6 h-6 border-4 border-white border-t-transparent rounded-full" data-id="element-157" /> : <>
              Claim my spot ☕ <ArrowRight className="w-6 h-6" data-id="element-158" />
            </>}
        </motion.button>
      </form>

      <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm px-5 py-3 rounded-full border-2 border-black font-medium text-sm" data-id="element-159">
        <div className="flex -space-x-2" data-id="element-160">
          {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-black bg-kraya-yellow flex items-center justify-center text-xs overflow-hidden" data-id="element-161">
              <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-162" />
            </div>)}
        </div>
        <span data-id="element-163">
          <strong className="font-bold" data-id="element-164">2,300+</strong> coffee lovers in line
        </span>
      </div>
    </motion.div>;
}